import {AfterViewInit, Component, OnInit, ViewChild} from '@angular/core';
import {Observable, of, Subject, forkJoin} from 'rxjs';
import {switchMap, map} from 'rxjs/operators';
import {ModalInterface} from '../../../shared/modal/modal.interface';
import {ModalStateEvent} from '../../../shared/modal/modal-state-event';
import {ModalState} from '../../../shared/modal/modal-state.enum';
import {MediaUploaderComponent} from '../../../shared/media/media-uploader/media-uploader.component';
import {MediaObjectRestService} from '../../../api/services/media-object.rest.service';
import {MediaObjectApiModel} from '../../../api/models/media-object.api-model';
import {PostRestService} from '../../../api/services/post-rest.service';
import {PostApiModel} from '../../../api/models/post-api.model';
import {PostTypeEnum} from '../../../core/enums/post-type.enum';
import {PostViewModel} from '../../../shared/timeline/post.view-model';
import {ResponseNotificationService} from '../../../core/services/response-notification.service';
import {TranslateService} from '@ngx-translate/core';
import {UploadTaskTrackerService} from '../../../core/services/upload-task-tracker.service';

@Component({
  selector: 'app-collaboration-media-post-create',
  templateUrl: './collaboration-media-post-create.component.html',
  styleUrls: ['./collaboration-media-post-create.component.scss']
})
export class CollaborationMediaPostCreateComponent implements OnInit, AfterViewInit, ModalInterface {
  public data: any;
  public subject: Subject<ModalStateEvent>;
  public state: Observable<ModalStateEvent>;
  public content = '';
  public historyEntryContent = '';
  public copyMediaContentToHistoryContent = false;
  public createHistoryEntryOnApproval = false;
  public startDate: Date | null = null;
  public endDate: Date | null = null;
  public isLoading = false;
  public isEditMode = false;
  @ViewChild('mediaUploaderComponent') public mediaUploaderComponent: MediaUploaderComponent;

  constructor(
    private readonly mediaObjectRS: MediaObjectRestService,
    private readonly postRS: PostRestService,
    private readonly notifications: ResponseNotificationService,
    private readonly translate: TranslateService,
    private readonly uploadTaskTracker: UploadTaskTrackerService,
  ) {}

  ngOnInit(): void {
    this.isEditMode = !!this.data?.post;
    if (this.isEditMode) {
      this.content = this.data?.post?.content || '';
      this.historyEntryContent = this.data?.post?.historyEntryContent || this.data?.post?.historyPost?.content || '';
      this.createHistoryEntryOnApproval = !!this.data?.post?.createHistoryEntryOnApproval || !!this.data?.post?.historyPost;
      this.startDate = this.data?.post?.startDate ? new Date(this.data.post.startDate) : new Date();
      this.endDate = this.data?.post?.endDate ? new Date(this.data.post.endDate) : this.startDate;
    } else {
      this.startDate = new Date();
      this.endDate = this.startDate;
    }

    this.subject.next(new ModalStateEvent(ModalState.VALID));
    this.state.subscribe((mse: ModalStateEvent) => {
      if (mse.status === ModalState.PENDING) {
        this.submit();
      }
    });
  }

  ngAfterViewInit(): void {
    if (!this.isEditMode || !this.mediaUploaderComponent?.fileUploader) {
      return;
    }

    setTimeout(() => {
      this.mediaUploaderComponent.selectMedia(this.mediaUploaderComponent.fileUploader);
    }, 0);
  }

  public submit(): void {
    const collaborationIri = this.data?.collaboration?.['@id'];
    const files = this.mediaUploaderComponent?.newFiles || [];
    if (!collaborationIri || this.isLoading) {
      this.notifications.emitMessage(this.translate.instant('features.user.collaboration.create.error_required'));
      return;
    }

    if (this.uploadTaskTracker.hasPendingTasks()) {
      this.notifications.emitMessage(this.translate.instant('features.user.collaboration.detail.error_upload'));
      return;
    }
    if (!this.isEditMode && !files.length) {
      this.notifications.emitMessage(this.translate.instant('features.user.collaboration.create.error_required'));
      return;
    }
    const existingMediaCount = this.mediaUploaderComponent?.mediaObjectAMs?.length || 0;
    if (this.isEditMode && (existingMediaCount + files.length) === 0) {
      this.notifications.emitMessage(this.translate.instant('features.user.collaboration.detail.media_required'));
      return;
    }

    this.isLoading = true;
    this.subject.next(new ModalStateEvent(ModalState.PROCESS));
    this.uploadSelectedMedia().pipe(
      switchMap((mediaIris) => {
        const payload = this.buildPayload(collaborationIri, mediaIris);
        if (this.isEditMode) {
          return this.postRS.put(payload).pipe(map((post) => new PostViewModel(post)));
        }
        return this.postRS.create(payload).pipe(map((post) => new PostViewModel(post)));
      })
    ).subscribe({
      next: (post) => {
        this.notifications.emitMessage(this.translate.instant(
          this.isEditMode
            ? 'features.user.collaboration.detail.post_update_success'
            : 'features.user.collaboration.detail.post_create_success'
        ));
        this.subject.next(new ModalStateEvent(ModalState.SUCCESS, post));
      },
      error: () => {
        this.notifications.emitMessage(this.translate.instant(
          this.isEditMode
            ? 'features.user.collaboration.detail.post_update_error'
            : 'features.user.collaboration.detail.error_upload'
        ));
        this.subject.next(new ModalStateEvent(ModalState.VALID));
      }
    }).add(() => {
      this.isLoading = false;
      this.subject.next(new ModalStateEvent(ModalState.PROCESS_END));
    });
  }

  private uploadSelectedMedia(): Observable<string[]> {
    const files = this.mediaUploaderComponent?.newFiles || [];
    if (!files.length) {
      return of([]);
    }

    const requests = files.map((file) => {
      const isVideo = file.type?.startsWith('video/');
      return isVideo ? this.mediaObjectRS.uploadVideo(file) : this.mediaObjectRS.uploadImage(file);
    });

    return forkJoin(requests).pipe(map((mediaObjects: MediaObjectApiModel[]) => mediaObjects.map((item) => item['@id'])));
  }

  private buildPayload(collaborationIri: string, uploadedMediaIris: string[]): PostApiModel {
    const payload = new PostApiModel();
    const postToEdit = this.data?.post as PostViewModel | undefined;
    const existingMediaIris = this.isEditMode
      ? (this.mediaUploaderComponent?.mediaObjectAMs || []).map((media: any) => (typeof media === 'string' ? media : media?.['@id'])).filter((iri) => !!iri)
      : [];

    payload['@id'] = postToEdit?.['@id'] || null;
    payload.type = PostTypeEnum.COLLABORATION_MEDIA_POST;
    payload.content = (this.content || '').trim();
    payload.collaboration = collaborationIri;
    payload.media = [...existingMediaIris, ...uploadedMediaIris];
    payload.createHistoryEntryOnApproval = !!this.createHistoryEntryOnApproval || !!postToEdit?.historyPost;
    payload.startDate = this.startDate || null;
    payload.endDate = this.endDate || this.startDate || null;
    payload.historyEntryContent = (this.historyEntryContent || '').trim() || null;

    return payload;
  }

  public isHistoryCheckboxLocked(): boolean {
    return this.isEditMode && !!this.data?.post?.historyPost;
  }

  public copyToHistoryContent(): void {
    this.historyEntryContent = (this.content || '').trim();
  }
}
